import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { entryActions } from "../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { getTypedCRUDErrors } from "../../../utils/getTypedCRUDErros";
import { Info } from "../../../components/Info";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { useDrawersContext } from "../../tasks/drawers-context";
import { AtiraCheckBoxGroup } from "../../../components/AtiraCheckBoxGroup";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { EntriesTableSettings } from "../../../model/entries-table-settings/EntriesTableSettings";
import { getTranslatedTableColumnLabel } from "../utils/getTranslatedTableColumnLabel";
import { countCsvRows } from "../utils/tables.util";
import { getEntriesExportFileName } from "../utils/getEntriesExportFileName";
import { ENTRIES_EXPORT_LIMIT } from "../utils/entriesExportLimit";

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  list-style-position: inside;
`;

const StyledAtiraCheckBoxGroup = styled(AtiraCheckBoxGroup)`
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Spacing.s};

  .ant-checkbox-wrapper {
    flex: 1 1 45%;
    white-space: normal;
  }
`;

export const EntriesExportTab: React.FC = () => {
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );

  const allColumns =
    tableSettings?.columns.filter(
      (col) => !["actions", "assignee", "task", "tags"].includes(col.field),
    ) || [];

  const [selectedHeaders, setSelectedHeaders] =
    useState<EntriesTableSettings["columns"]>(allColumns);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { setImportExportVisible } = useDrawersContext();

  const onSetErrors = (e: any) => {
    const errors = getTypedCRUDErrors(e);

    if (errors?.length) {
      // A typed error's message is a translation key, its params fill it in
      setErrorMessage(t(errors[0].message, errors[0].params));
    } else if (e.response?.data) {
      setErrorMessage(t(e.response.data));
    }
  };

  const onExport = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        entryActions.exportEntries({
          userId,
          selectedHeaders: selectedHeaders.map((col) => col.field),
        }),
      ).unwrap();

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        getEntriesExportFileName({
          count: countCsvRows(response),
          scope: "all",
        }),
      );

      document.body.appendChild(link);
      link.click();

      AtiraToast.success(t("entries.export.success"));

      link.parentNode?.removeChild(link);

      setImportExportVisible(false);
      setErrorMessage("");
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetErrors(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex flexDirection="column" gap="m">
      <Info info={t("entries.export.description")} />
      <Info
        color="warning"
        info={
          <StyledList>
            <li>{t("entries.export.modal.description")}</li>
            <li>{t("entries.export.modal.select.description")}</li>
            <li>
              {t("entries.export.modal.limit.description", {
                limit: ENTRIES_EXPORT_LIMIT.toLocaleString("en-US"),
              })}
            </li>
          </StyledList>
        }
      />
      <Flex flexDirection="column" gap="m">
        <Flex flexDirection="column">
          <Text fontWeight="bold">
            {t("entries.export.modal.selectHeaders.title")}
          </Text>
          <Text fontSize="s" color="darkerSub">
            {t("entries.export.modal.selectHeaders.description")}
          </Text>
        </Flex>

        <StyledAtiraCheckBoxGroup
          options={allColumns.map((col) => ({
            label: getTranslatedTableColumnLabel(col),
            value: col.field,
          }))}
          value={selectedHeaders.map((col) => col.field)}
          style={{ flexWrap: "wrap" }}
          columnGap="7rem"
          onChange={(values) =>
            setSelectedHeaders(
              allColumns.filter((col) => values.includes(col.field)),
            )
          }
        />
      </Flex>

      {errorMessage ? <Text color="danger">* {errorMessage}</Text> : null}
      <Button
        disabled={!selectedHeaders.length}
        loading={loading}
        title={t("entries.export.modal.button")}
        onClick={onExport}
        margin="none"
      />
    </Flex>
  );
};
